import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store/hooks';
import { closeScanner } from '../store/slices/navigationSlice';
import { ExhibitId } from '../store/slices/exhibitSlice';

// Dynamic import for QrScanner to handle potential loading issues
let QrScanner: any = null;

export default function QrScannerPage() {
    console.log('🚀 QR Scanner component loaded - NEW VERSION with enhanced debugging');
    
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [scanner, setScanner] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [scanning, setScanning] = useState(false);
    const [qrScannerLoaded, setQrScannerLoaded] = useState(false);
    
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    // Load QrScanner dynamically
    useEffect(() => {
        const loadQrScanner = async () => {
            try {
                const QrScannerModule = await import('qr-scanner');
                QrScanner = QrScannerModule.default;
                setQrScannerLoaded(true);
            } catch (err) {
                console.error('Failed to load QrScanner library:', err);
                setError('Failed to load QR scanner library');
            }
        };
        
        loadQrScanner();
    }, []);
    
    useEffect(() => {
        if (videoRef.current && !scanner && qrScannerLoaded && QrScanner) {
            try {
                // Initialize QR scanner
                const newScanner = new QrScanner(
                    videoRef.current,
                    (result: any) => {
                        handleScan(result.data);
                    },
                    {
                        returnDetailedScanResult: true,
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                        preferredCamera: 'environment',
                    }
                );
                
                setScanner(newScanner);
                
                newScanner.start().then(() => {
                    setScanning(true);
                }).catch((err: any) => {
                    console.error('Failed to start scanner:', err);
                    setError(t('qrScanner.cameraError'));
                });
                
                return () => {
                    newScanner.destroy();
                };
            } catch (err) {
                console.error('Failed to initialize scanner:', err);
                setError(t('qrScanner.initError'));
            }
        }
        
        return () => {
            if (scanner) {
                scanner.destroy();
            }
        };
    }, [t, qrScannerLoaded]);
    
    const handleScan = (data: string) => {
        // Stop scanning once we get a result
        if (scanner) {
            scanner.stop();
        }
        
        // Process the QR code data
        try {
            // Check if the QR code data is a valid URL
            const url = new URL(data);
            
            let pathParts: string[] = [];
            let isValidTimeCapsuleUrl = false;
            
            // Handle both timecapsule:// protocol and https:// URLs
            if (url.protocol === 'timecapsule:') {
                // Expected format: timecapsule://exhibit/[exhibit-id]
                pathParts = url.pathname.split('/').filter(Boolean);
                isValidTimeCapsuleUrl = true;
            } else if (url.protocol === 'https:' && (url.hostname.includes('timecapsule') || url.hostname.includes('untitledproject'))) {
                // Handle https://timecapsule.untitledproject.sg/staging/exhibit/[exhibit-id] format
                pathParts = url.pathname.split('/').filter(Boolean);
                // Remove 'staging' if present
                if (pathParts[0] === 'staging') {
                    pathParts = pathParts.slice(1);
                }
                isValidTimeCapsuleUrl = true;
            }
            
            if (isValidTimeCapsuleUrl && pathParts[0] === 'exhibit' && pathParts[1]) {
                const exhibitId = pathParts[1] as ExhibitId;
                
                // Close the scanner in the navigation state
                dispatch(closeScanner());
                
                // Check if this is a zone-based exhibit that needs automatic zone claiming
                const zoneBasedExhibits = ['orang-laut', 'market-traders', 'early-market-traders', 'peranakan-patterns', 'peranakan-design'];
                const isZoneBased = zoneBasedExhibits.includes(exhibitId);
                
                if (isZoneBased) {
                    // For zone-based exhibits, navigate with fromQrScan parameter and timestamp
                    // The timestamp ensures the useEffect triggers even for repeated scans of the same QR code
                    const timestamp = Date.now().toString();
                    
                    // Special case: For peranakan-patterns or peranakan-design QR scans, navigate to peranakan-design
                    // This allows users to start from the intro page and then navigate to the interactive tiles
                    if (exhibitId === 'peranakan-patterns' || exhibitId === 'peranakan-design') {
                        const targetUrl = `/exhibit/peranakan-design?fromQrScan=true&qrTimestamp=${timestamp}`;
                        navigate(targetUrl);
                    } else if (exhibitId === 'early-market-traders') {
                        // Map early-market-traders to market-traders route with QR scan params
                        const targetUrl = `/exhibit/market-traders?fromQrScan=true&qrTimestamp=${timestamp}`;
                        navigate(targetUrl);
                    } else {
                        const targetUrl = `/exhibit/${exhibitId}?fromQrScan=true&qrTimestamp=${timestamp}`;
                        navigate(targetUrl);
                    }
                } else {
                    // For non-zone exhibits, navigate normally
                    const targetUrl = `/exhibit/${exhibitId}`;
                    navigate(targetUrl);
                }
                return;
            }
            
            // If we get here, it's not a valid Time Capsule QR code
            setError(t('qrScanner.invalidQrCode'));
            
            // Restart scanning after a delay
            setTimeout(() => {
                if (scanner) {
                    scanner.start();
                }
            }, 3000);
            
        } catch (err) {
            console.error('QR code parsing error:', err);
            setError(t('qrScanner.invalidFormat'));
            
            // Restart scanning after a delay
            setTimeout(() => {
                if (scanner) {
                    scanner.start();
                }
            }, 3000);
        }
    };
    
    return (
        <div className="p-6 max-w-md mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">{t('qrScanner.title')}</h2>
            
            {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                    {error}
                </div>
            )}
            
            <div className="relative mx-auto mb-6">
                <div className="w-64 h-64 mx-auto overflow-hidden rounded-lg border-2 border-blue-500">
                    <video 
                        ref={videoRef} 
                        className="w-full h-full object-cover"
                    />
                </div>
                
                {scanning && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-40 h-40 border-2 border-blue-500 opacity-70 animate-pulse"></div>
                    </div>
                )}
            </div>
            
            <p className="mb-6">
                {t('qrScanner.instructions')}
            </p>
            
            <button 
                onClick={() => navigate('/')}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded"
            >
                {t('common.cancel')}
            </button>
        </div>
    );
}
